import { useEffect, useRef, useState } from 'react';

/**
 * Counts from 0 to `target` when the returned ref scrolls into
 * view. Jumps straight to the final value when the visitor
 * prefers reduced motion.
 */
const useCountUp = (target: number, durationMs = 1600) => {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const run = (): void => {
      if (started.current) return;
      started.current = true;

      if (reduced) {
        setValue(target);
        return;
      }

      const start = performance.now();
      const tick = (now: number): void => {
        const t = Math.min((now - start) / durationMs, 1);
        // ease-out-cubic
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(Math.round(eased * target));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    if (!('IntersectionObserver' in window)) {
      run();
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          run();
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, durationMs]);

  return { ref, value };
};

export default useCountUp;
